import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { GraduationCap, Upload, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobApplicationPublic() {
  const [formData, setFormData] = useState({
    applicant_name: '',
    email: '',
    phone: '',
    position: '',
    cover_letter: '',
  });
  const [resume, setResume] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      if (!['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
        toast.error('Only PDF and Word documents are allowed');
        return;
      }
      setResume(file);
      setResumeFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resume) {
      toast.error('Please upload your resume');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('applicant_name', formData.applicant_name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('cover_letter', formData.cover_letter);
      formDataToSend.append('resume', resume);

      await axios.post(
        `${BACKEND_URL}/api/job-applications/with-resume`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4">
        <Card className="w-full max-w-2xl text-center">
          <CardContent className="pt-12 pb-12">
            <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope' }}>
              Application Submitted!
            </h2>
            <p className="text-[#64748B] mb-6">
              Thank you for applying. We'll review your application and get back to you soon.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  applicant_name: '',
                  email: '',
                  phone: '',
                  position: '',
                  cover_letter: '',
                });
                setResume(null);
                setResumeFileName('');
              }}
              className="bg-[#0F172A] hover:bg-[#1E293B]"
            >
              Submit Another Application
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0F172A] rounded-md mb-4">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight mb-2" style={{ fontFamily: 'Manrope' }}>
            Teaching Position Application
          </h1>
          <p className="text-[#64748B]">Join our team of dedicated educators</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Application Form</CardTitle>
            <CardDescription>Please fill in your details and upload your resume</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="applicant_name">Full Name *</Label>
                  <Input
                    id="applicant_name"
                    data-testid="applicant-name-input"
                    value={formData.applicant_name}
                    onChange={(e) => setFormData({ ...formData, applicant_name: e.target.value })}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid="applicant-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    data-testid="applicant-phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="+1234567890"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Position *</Label>
                  <Input
                    id="position"
                    data-testid="applicant-position-input"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    required
                    placeholder="Mathematics Teacher"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cover_letter">Cover Letter *</Label>
                <Textarea
                  id="cover_letter"
                  data-testid="applicant-cover-letter-input"
                  value={formData.cover_letter}
                  onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
                  required
                  placeholder="Tell us why you'd be a great fit for this position..."
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resume">Resume/CV * (PDF or Word, max 10MB)</Label>
                <div className="flex items-center gap-4">
                  <input
                    id="resume"
                    data-testid="resume-upload-input"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('resume').click()}
                    className="w-full"
                    data-testid="resume-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {resumeFileName || 'Choose File'}
                  </Button>
                </div>
                {resumeFileName && (
                  <p className="text-sm text-[#10B981]">✓ {resumeFileName}</p>
                )}
              </div>

              <Button
                type="submit"
                data-testid="submit-application-btn"
                className="w-full bg-[#0F172A] hover:bg-[#1E293B]"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#64748B] mt-6">
          Already have an account? <a href="/" className="text-[#0F172A] font-semibold hover:underline">Login here</a>
        </p>
      </div>
    </div>
  );
}
